/**
 * Claim refusals for a closed company (Phase 119-07).
 *
 * Every claim path (onboarding-claim, verify-company-claim, onboarding-join,
 * claim-submission, team-accept-invite) answers a closed company with
 * 409 `{ code: 'company_closed' }`, and a company whose last founder's erasure
 * is still running with 409 `{ code: 'erasure_hold' }` (119-03). The client
 * branches on the CODE, never on the message text, and shows these words.
 *
 * A plain `new Error(message)` would not survive `handleError` →
 * `getUserMessage`, which replaces unclassified messages with a generic one —
 * so the refusal is a typed error that carries its own user message.
 */

export type ClaimRefusalCode = 'company_closed' | 'erasure_hold';

export const CLAIM_REFUSAL_MESSAGES: Record<ClaimRefusalCode, string> = {
  company_closed: "This company has been closed and can't be claimed. Contact support if you believe this is wrong.",
  erasure_hold: 'Try again later.',
};

export class ClaimRefusalError extends Error {
  readonly code: ClaimRefusalCode;
  readonly userMessage: string;

  constructor(code: ClaimRefusalCode) {
    super(`claim_refused_${code}`);
    this.name = 'ClaimRefusalError';
    this.code = code;
    this.userMessage = CLAIM_REFUSAL_MESSAGES[code];
  }
}

export function isClaimRefusalCode(code: unknown): code is ClaimRefusalCode {
  return code === 'company_closed' || code === 'erasure_hold';
}

/** The typed refusal for a response code, or null for any other code. */
export function claimRefusalFor(code: unknown): ClaimRefusalError | null {
  return isClaimRefusalCode(code) ? new ClaimRefusalError(code) : null;
}

/** The words to show for a caught error, when it is a lifecycle refusal. */
export function claimRefusalMessage(err: unknown): string | null {
  if (err instanceof ClaimRefusalError) return err.userMessage;
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  return isClaimRefusalCode(code) ? CLAIM_REFUSAL_MESSAGES[code] : null;
}
